import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhoneVolume, faUsers, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { API_URL } from "../config";
import { showToast } from "../components/toast";

const AICampaignModal = ({
  isOpen,
  onClose,
  selectedLeads = [],
  selectedCount = 0,
  onSuccess,
}) => {
  const [mode, setMode] = useState("new"); // "new" | "existing"
  const [existingCampaigns, setExistingCampaigns] = useState([]);
  const [selectedExistingCampaignId, setSelectedExistingCampaignId] = useState("");
  const [fetchingCampaigns, setFetchingCampaigns] = useState(false);

  const [name, setName] = useState("");
  const [segment, setSegment] = useState("no_response");
  const [language, setLanguage] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [allowedStartTime, setAllowedStartTime] = useState("09:30");
  const [allowedEndTime, setAllowedEndTime] = useState("19:00");
  const [allowedDays, setAllowedDays] = useState([
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ]);
  const [retry, setRetry] = useState(1);
  const [retryIntervalMinutes, setRetryIntervalMinutes] = useState(30);
  const [retryBusy, setRetryBusy] = useState(true);
  const [retryNoAnswer, setRetryNoAnswer] = useState(true);
  const [retryFailed, setRetryFailed] = useState(true);
  const [attemptsPerSecond, setAttemptsPerSecond] = useState(0.1);

  const allWeekDays = [
    { label: "Mon", value: "Monday" },
    { label: "Tue", value: "Tuesday" },
    { label: "Wed", value: "Wednesday" },
    { label: "Thu", value: "Thursday" },
    { label: "Fri", value: "Friday" },
    { label: "Sat", value: "Saturday" },
    { label: "Sun", value: "Sunday" },
  ];

  const toggleDay = (dayValue) => {
    setAllowedDays((prev) =>
      prev.includes(dayValue)
        ? prev.filter((d) => d !== dayValue)
        : [...prev, dayValue]
    );
  };

  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [categories, setCategories] = useState([]);
  const [agents, setAgents] = useState([]);
  const [connections, setConnections] = useState([]);
  const [numberGroups, setNumberGroups] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [selectedNumberGroupId, setSelectedNumberGroupId] = useState("");

  const fetchExistingCampaigns = async () => {
    try {
      setFetchingCampaigns(true);
      const res = await axios.get(`${API_URL}voiceCall/campaigns?limit=100`, getAuthHeader());
      const list = res.data?.items || [];
      // Filter campaigns that can accept contacts (running, scheduled, launching, active, paused)
      const activeList = list.filter((c) =>
        ["running", "scheduled", "launching", "paused", "active"].includes(
          (c.status || "").toLowerCase()
        )
      );
      setExistingCampaigns(activeList.length > 0 ? activeList : list);
      if (activeList.length > 0) {
        setSelectedExistingCampaignId(String(activeList[0].id));
      } else if (list.length > 0) {
        setSelectedExistingCampaignId(String(list[0].id));
      }
    } catch (err) {
      console.warn("Could not load existing campaigns:", err);
    } finally {
      setFetchingCampaigns(false);
    }
  };

  const fetchSetupOptions = async () => {
    try {
      const [catRes, agentRes, connRes, groupRes] = await Promise.allSettled([
        axios.get(`${API_URL}voiceCall/categories`, getAuthHeader()),
        axios.get(`${API_URL}voiceCall/agents`, getAuthHeader()),
        axios.get(`${API_URL}voiceCall/connections`, getAuthHeader()),
        axios.get(`${API_URL}voiceCall/number-groups`, getAuthHeader()),
      ]);

      if (catRes.status === "fulfilled" && Array.isArray(catRes.value.data)) {
        setCategories(catRes.value.data);
      }
      if (agentRes.status === "fulfilled" && Array.isArray(agentRes.value.data)) {
        setAgents(agentRes.value.data);
        const defaultAgent = agentRes.value.data.find((a) => a.is_default) || agentRes.value.data[0];
        if (defaultAgent) setSelectedAgentId(String(defaultAgent.id));
      }
      if (connRes.status === "fulfilled" && Array.isArray(connRes.value.data)) {
        setConnections(connRes.value.data);
        const defaultConn = connRes.value.data.find((c) => c.is_default) || connRes.value.data[0];
        if (defaultConn) setSelectedConnectionId(defaultConn.connection_id);
      }
      if (groupRes.status === "fulfilled" && Array.isArray(groupRes.value.data)) {
        setNumberGroups(groupRes.value.data);
      }
    } catch (err) {
      console.warn("Could not load setup options from voice server:", err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const today = new Date();
      const date = `${String(today.getDate()).padStart(2, "0")}-${String(
        today.getMonth() + 1
      ).padStart(2, "0")}-${today.getFullYear()}`;
      setName(`AI Campaign - ${date}`);
      setPreviewResult(null);
      setMode("new");
      setScheduleAt("");
      setEndAt("");
      setAllowedStartTime("09:30");
      setAllowedEndTime("19:00");
      setAllowedDays(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]);
      setRetry(1);
      setRetryIntervalMinutes(30);
      setRetryBusy(true);
      setRetryNoAnswer(true);
      setRetryFailed(true);
      fetchSetupOptions();
      fetchExistingCampaigns();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const getAuthHeader = () => {
    const token = localStorage.getItem("authToken");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  };

  const buildCampaignPayloadData = () => {
    const leadsPayload = selectedLeads.map((u) => ({
      sub_uid: String(u.id || u.subuid || ""),
      mobile: u.mobileno || u.mobile || "",
      name: u.fullname || u.name || "Customer",
      language: language || u.language || null,
      lead_source: u.lead_source || null,
      lead_status: u.lead_status || null,
      agent_vars: {
        // segment_registered: u.interested_segments || "Index Options",
        // last_plan: u.last_plan || "",
        // expiry_date: u.expiry_date || "",
        // created_date: u.created_on || "",
        // recent_contacted: u.recent_contacted || "",
      },
    }));

    const selectedAgentObj = agents.find(
      (a) => String(a.id) === String(selectedAgentId) || a.app_id === selectedAgentId
    );

    return {
      name: name.trim() || "AI Campaign",
      segment,
      language: language || undefined,
      agent: selectedAgentObj?.id ? Number(selectedAgentObj.id) : (selectedAgentId ? Number(selectedAgentId) : undefined),
      app_id: selectedAgentObj?.app_id || undefined,
      connection_id: selectedConnectionId || undefined,
      number_group_id: selectedNumberGroupId ? Number(selectedNumberGroupId) : undefined,
      schedule_at: scheduleAt ? new Date(scheduleAt).toISOString() : undefined,
      end_at: endAt ? new Date(endAt).toISOString() : undefined,
      allowed_schedule: {
        allowed_days: allowedDays,
        allowed_start_time: allowedStartTime || "09:30",
        allowed_end_time: allowedEndTime || "19:00",
        timezone: "Asia/Kolkata",
      },
      app_config: {
        app_id: selectedAgentObj?.app_id || (selectedAgentId ? String(selectedAgentId) : undefined),
        app_version: selectedAgentObj?.default_version || selectedAgentObj?.versions?.[0] || undefined,
        attempts_per_second: Number(attemptsPerSecond) || 0.1,
        retry: {
          enabled: Number(retry) > 0,
          max_retries: Number(retry) >= 0 ? Number(retry) : 1,
          interval_minutes: [Number(retryIntervalMinutes) || 30],
          busy: retryBusy,
          no_answer: retryNoAnswer,
          failed: retryFailed,
          short_duration: { enabled: false, threshold_seconds: 30 },
          provider_error: { enabled: false, max_retries: 3, retry_interval_minutes: 5 },
          internal_error: { enabled: false, max_retries: 3, retry_interval_minutes: 5 },
        },
        phone_rotation: { agent: false },
      },
      retry: Number(retry) >= 0 ? Number(retry) : 1,
      retry_interval_minutes: Number(retryIntervalMinutes) || 30,
      retry_busy: retryBusy,
      retry_no_answer: retryNoAnswer,
      retry_failed: retryFailed,
      attempts_per_second: Number(attemptsPerSecond) || 0.1,
      leads: leadsPayload,
    };
  };

  const handleDryRunPreview = async () => {
    try {
      setPreviewLoading(true);
      const payload = buildCampaignPayloadData();

      const res = await axios.post(
        `${API_URL}voiceCall/campaigns/preview`,
        payload,
        getAuthHeader()
      );

      setPreviewResult(res.data);
      if (res.data?.ok === false) {
        showToast.warning("Preview returned issues. Please review before launching.");
      } else {
        showToast.success("Audience preview verified successfully!");
      }
    } catch (err) {
      console.error("Preview error:", err);
      showToast.error(
        err?.response?.data?.message || err?.response?.data?.detail || "Failed to preview audience"
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedLeads.length && selectedCount === 0) {
      showToast.warning("Please select at least one lead.");
      return;
    }

    if (mode === "existing") {
      if (!selectedExistingCampaignId) {
        showToast.warning("Please select a campaign to add contacts to.");
        return;
      }

      try {
        setLoading(true);
        const usersPayload = selectedLeads.map((u) => ({
          user_identifier: String(u.id || u.subuid || ""),
          sub_uid: String(u.id || u.subuid || ""),
          user_phone_number: u.mobileno || u.mobile || "",
          mobile: u.mobileno || u.mobile || "",
          name: u.fullname || u.name || "Customer",
          lead_status: u.lead_status || "new",
          app_variables: {
            // segment_registered: u.interested_segments || "Index Options",
            // last_plan: u.last_plan || "",
            // expiry_date: u.expiry_date || "",
            // created_date: u.created_on || "",
            // recent_contacted: u.recent_contacted || "",
          },
        }));

        const res = await axios.post(
          `${API_URL}voiceCall/campaigns/${selectedExistingCampaignId}/contacts`,
          {
            users: usersPayload,
            mobiles: selectedLeads
              .map((u) => u.mobileno || u.mobile)
              .filter(Boolean),
          },
          getAuthHeader()
        );

        showToast.success(
          res.data?.message || "Successfully added leads to campaign!"
        );
        if (onSuccess) onSuccess(res.data);
        onClose();
      } catch (err) {
        console.error("Failed to add contacts to campaign:", err);
        showToast.error(
          err?.response?.data?.message || "Failed to add contacts to campaign"
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!name.trim()) {
      showToast.warning("Please provide a campaign name.");
      return;
    }

    try {
      setLoading(true);
      const payload = buildCampaignPayloadData();

      console.log("🚀 [FRONTEND] Launching AI Campaign Payload Object:", payload);
      console.log("🚀 [FRONTEND] Final JSON Payload:\n" + JSON.stringify(payload, null, 2));

      const res = await axios.post(
        `${API_URL}voiceCall/campaigns`,
        payload,
        getAuthHeader()
      );

      showToast.success(
        res.data?.message || "AI Voice Campaign launched successfully!"
      );
      onClose();
      if (onSuccess) {
        try {
          onSuccess(res.data);
        } catch (callbackErr) {
          console.error("onSuccess callback error:", callbackErr);
        }
      }
    } catch (err) {
      console.error("Failed to launch campaign:", err);
      showToast.error(
        err?.response?.data?.message || "Failed to launch campaign"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        tabIndex="-1"
        role="dialog"
        onClick={onClose}
      >
        <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header d-flex justify-content-between align-items-center">
              <div>
                <h5 className="modal-title font-weight-bold d-inline-block">AI Voice Call Campaign</h5>
                <span className="text-muted ms-2" style={{ fontSize: "0.85rem" }}>
                  ({selectedCount || selectedLeads.length} leads selected)
                </span>
              </div>
              <button
                type="button"
                className="btn btn-default float-end"
                onClick={onClose}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Action Type Selector - Simple Normal UI */}
                <div className="form-group mb-3">
                  <label className="control-label font-weight-bold">
                    Action Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                  >
                    <option value="new">Create New Campaign</option>
                    <option value="existing">Add to Running / Active Campaign</option>
                  </select>
                </div>

                {mode === "existing" ? (
                  <div className="p-3 bg-light border rounded mb-3">
                    <div className="form-group mb-3">
                      <label className="control-label font-weight-bold">
                        Select Running / Scheduled Campaign <span className="text-danger">*</span>
                      </label>
                      {fetchingCampaigns ? (
                        <div className="text-muted py-2 small">Loading active campaigns...</div>
                      ) : existingCampaigns.length > 0 ? (
                        <select
                          className="form-control"
                          value={selectedExistingCampaignId}
                          onChange={(e) => setSelectedExistingCampaignId(e.target.value)}
                          required
                        >
                          {existingCampaigns.map((c) => (
                            <option key={c.id} value={c.id}>
                              #{c.id} - {c.campaignName || "Untitled Campaign"} ({c.status || "active"} | {c.reachedCount || 0}/{c.totalLeads || 0} reached)
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="alert alert-warning py-2 mb-0 small">
                          No active or running campaigns found. Please create a new campaign.
                        </div>
                      )}
                    </div>

                    {existingCampaigns.find((c) => String(c.id) === String(selectedExistingCampaignId)) && (
                      (() => {
                        const selCamp = existingCampaigns.find((c) => String(c.id) === String(selectedExistingCampaignId));
                        return (
                          <div className="card p-3 small">
                            <div className="row">
                              <div className="col-md-6 mb-1">
                                <strong>Status:</strong>{" "}
                                <span className="badge badge-info text-capitalize text-dark bg-info-subtle">
                                  {selCamp.status}
                                </span>
                              </div>
                              <div className="col-md-6 mb-1">
                                <strong>Total Leads:</strong> {selCamp.totalLeads || 0} (Reached: {selCamp.reachedCount || 0})
                              </div>
                            </div>
                          </div>
                        );
                      })()
                    )}
                  </div>
                ) : (
                  <>
                <div className="form-group mb-3">
                  <label className="control-label font-weight-bold">
                    Campaign Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Campaign Follow-up"
                  />
                </div>

                <div className="row">
                  <div className="col-md-6 form-group mb-3">
                    <label className="control-label font-weight-bold">
                      AI Voice Agent <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={selectedAgentId}
                      onChange={(e) => setSelectedAgentId(e.target.value)}
                    >
                      {agents.length > 0 ? (
                        agents.map((ag) => (
                          <option key={ag.id} value={ag.id}>
                            {ag.name || ag.app_id} {ag.is_default ? "(Default)" : ""}
                          </option>
                        ))
                      ) : (
                        <option value="">Default Agent</option>
                      )}
                    </select>
                  </div>

                  <div className="col-md-6 form-group mb-3">
                    <label className="control-label font-weight-bold">
                      Phone Line / Number Group
                    </label>
                    <select
                      className="form-control"
                      value={selectedNumberGroupId || selectedConnectionId}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.startsWith("group:")) {
                          setSelectedNumberGroupId(val.replace("group:", ""));
                        } else {
                          setSelectedNumberGroupId("");
                          setSelectedConnectionId(val);
                        }
                      }}
                    >
                      {connections.length > 0 ? (
                        connections.map((c) => (
                          <option key={c.id || c.connection_id} value={c.connection_id}>
                            Line: {c.name || c.connection_id} {c.is_default ? "(Default)" : ""}
                          </option>
                        ))
                      ) : (
                        <option value="">Default Connection Line</option>
                      )}
                      {numberGroups.length > 0 && (
                        <optgroup label="Number Groups">
                          {numberGroups.map((g) => (
                            <option key={`grp-${g.id}`} value={`group:${g.id}`}>
                              Group: {g.name} ({g.phone_numbers?.length || 0} numbers)
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                </div>

                <div className="form-group mb-3">
                  <label className="control-label font-weight-bold">
                    Script / Calling Segment <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={segment}
                    onChange={(e) => setSegment(e.target.value)}
                  >
                    <option value="no_response">No Response — Build interest & warm up</option>
                    <option value="not_interested">Not Interested — Understand reason & re-engage</option>
                    <option value="free_trial_expired">Free Trial Expired — Offer extension / discount</option>
                    <option value="lead_welcome">Lead Welcome — Welcome & check market interest</option>
                    {categories.length > 0 && (
                      <optgroup label="Voice Server Categories">
                        {categories.map((cat, idx) => (
                          <option key={idx} value={typeof cat === "string" ? cat : cat.name || cat.id}>
                            {typeof cat === "string" ? cat : cat.name || cat.id}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>

                <div className="row">
                  <div className="col-md-6 form-group mb-3">
                    <label className="control-label">Preferred Language</label>
                    <select
                      className="form-control"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                    >
                      <option value="">Default (Lead preference or Hindi)</option>
                      <option value="Hindi">Hindi (hi-IN)</option>
                      <option value="English">English (en-IN)</option>
                      <option value="Hinglish">Hinglish</option>
                      <option value="Gujarati">Gujarati (gu-IN)</option>
                      <option value="Marathi">Marathi (mr-IN)</option>
                    </select>
                  </div>

                  <div className="col-md-6 form-group mb-3">
                    <label className="control-label font-weight-bold">Schedule Start Time</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={scheduleAt}
                      onChange={(e) => setScheduleAt(e.target.value)}
                    />
                    <small className="form-text text-muted">Leave empty to launch immediately</small>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 form-group mb-3">
                    <label className="control-label font-weight-bold">Schedule End Time</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={endAt}
                      onChange={(e) => setEndAt(e.target.value)}
                    />
                    <small className="form-text text-muted">Leave empty for default (+7 days)</small>
                  </div>

                  <div className="col-md-6 form-group mb-3">
                    <label className="control-label font-weight-bold">Calling Hours Window</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                      <input
                        type="time"
                        className="form-control"
                        value={allowedStartTime}
                        onChange={(e) => setAllowedStartTime(e.target.value)}
                        style={{ maxWidth: "150px" }}
                        title="Start calling daily at this time"
                      />
                      <span className="text-muted" style={{ padding: "0 4px", fontWeight: "bold" }}>to</span>
                      <input
                        type="time"
                        className="form-control"
                        value={allowedEndTime}
                        onChange={(e) => setAllowedEndTime(e.target.value)}
                        style={{ maxWidth: "150px" }}
                        title="Stop calling daily at this time"
                      />
                    </div>
                    <small className="form-text text-muted">Daily calling window (IST)</small>
                  </div>
                </div>

                <div className="form-group mb-3">
                  <label className="control-label font-weight-bold d-block" style={{ marginBottom: "6px" }}>
                    Allowed Calling Days
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                    {allWeekDays.map((d) => {
                      const isChecked = allowedDays.includes(d.value);
                      return (
                        <button
                          key={d.value}
                          type="button"
                          onClick={() => toggleDay(d.value)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "54px",
                            padding: "6px 14px",
                            borderRadius: "20px",
                            fontSize: "13px",
                            fontWeight: isChecked ? "600" : "500",
                            cursor: "pointer",
                            border: isChecked ? "1px solid #0d6efd" : "1px solid #ced4da",
                            backgroundColor: isChecked ? "#0d6efd" : "#fff",
                            color: isChecked ? "#fff" : "#495057",
                            boxShadow: isChecked ? "0 2px 4px rgba(13,110,253,0.25)" : "none",
                            transition: "all 0.15s ease-in-out",
                          }}
                        >
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4 form-group mb-3">
                    <label className="control-label">Retry Attempts</label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      className="form-control"
                      value={retry}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setRetry(isNaN(val) ? 0 : Math.max(0, val));
                      }}
                    />
                  </div>

                  <div className="col-md-4 form-group mb-3">
                    <label className="control-label">Retry Interval</label>
                    <select
                      className="form-control"
                      value={retryIntervalMinutes}
                      disabled={retry === 0}
                      onChange={(e) => setRetryIntervalMinutes(Number(e.target.value))}
                    >
                      <option value={15}>15 Minutes</option>
                      <option value={30}>30 Minutes</option>
                      <option value={45}>45 Minutes</option>
                      <option value={60}>1 Hour</option>
                      <option value={120}>2 Hours</option>
                    </select>
                  </div>

                  <div className="col-md-4 form-group mb-3">
                    <label className="control-label">Attempts Per Second</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      max="50"
                      className="form-control"
                      value={attemptsPerSecond}
                      onChange={(e) => setAttemptsPerSecond(parseFloat(e.target.value) || 0.1)}
                    />
                  </div>
                </div>

                {/* Retry Triggers (Pills / Toggle Style) */}
                <div
                  className="mb-3"
                  style={{
                    padding: "10px 14px",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                    borderRadius: "6px",
                    opacity: retry === 0 ? 0.5 : 1,
                  }}
                >
                  <label
                    className="control-label font-weight-bold d-block"
                    style={{ fontSize: "12px", marginBottom: "8px", color: "#495057" }}
                  >
                    Retry When Call Status Is:
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                    <button
                      type="button"
                      disabled={retry === 0}
                      onClick={() => setRetryBusy(!retryBusy)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "6px 14px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: retryBusy ? "600" : "500",
                        cursor: retry === 0 ? "not-allowed" : "pointer",
                        border: retryBusy ? "1px solid #0d6efd" : "1px solid #ced4da",
                        backgroundColor: retryBusy ? "#0d6efd" : "#fff",
                        color: retryBusy ? "#fff" : "#495057",
                        boxShadow: retryBusy ? "0 2px 4px rgba(13,110,253,0.2)" : "none",
                        transition: "all 0.15s ease-in-out",
                      }}
                    >
                      Customer Busy
                    </button>

                    <button
                      type="button"
                      disabled={retry === 0}
                      onClick={() => setRetryNoAnswer(!retryNoAnswer)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "6px 14px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: retryNoAnswer ? "600" : "500",
                        cursor: retry === 0 ? "not-allowed" : "pointer",
                        border: retryNoAnswer ? "1px solid #0d6efd" : "1px solid #ced4da",
                        backgroundColor: retryNoAnswer ? "#0d6efd" : "#fff",
                        color: retryNoAnswer ? "#fff" : "#495057",
                        boxShadow: retryNoAnswer ? "0 2px 4px rgba(13,110,253,0.2)" : "none",
                        transition: "all 0.15s ease-in-out",
                      }}
                    >
                      No Answer / Ringing
                    </button>

                    <button
                      type="button"
                      disabled={retry === 0}
                      onClick={() => setRetryFailed(!retryFailed)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "6px 14px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: retryFailed ? "600" : "500",
                        cursor: retry === 0 ? "not-allowed" : "pointer",
                        border: retryFailed ? "1px solid #0d6efd" : "1px solid #ced4da",
                        backgroundColor: retryFailed ? "#0d6efd" : "#fff",
                        color: retryFailed ? "#fff" : "#495057",
                        boxShadow: retryFailed ? "0 2px 4px rgba(13,110,253,0.2)" : "none",
                        transition: "all 0.15s ease-in-out",
                      }}
                    >
                      Call Failed / Dropped
                    </button>
                  </div>
                </div>

                {/* Pre-flight Audience check */}
                <div className="panel panel-default p-3 bg-light border mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="font-weight-bold" style={{ fontSize: "13px" }}>Audience Pre-check (Voice Server Preview)</span>
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      onClick={handleDryRunPreview}
                      disabled={previewLoading || (!selectedLeads.length && selectedCount === 0)}
                    >
                      {previewLoading ? "Checking..." : "Run Pre-check"}
                    </button>
                  </div>

                  {previewResult && (
                    <div className="small">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span>Preview Status:</span>
                        {previewResult.ok !== false ? (
                          <span className="badge badge-success text-success bg-light border border-success px-2 py-1">
                            ✓ Ready to launch (ok: true)
                          </span>
                        ) : (
                          <span className="badge badge-danger text-danger bg-light border border-danger px-2 py-1">
                            ⚠ Problems found (ok: false)
                          </span>
                        )}
                      </div>

                      {Array.isArray(previewResult.problems) && previewResult.problems.length > 0 && (
                        <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: "12px" }}>
                          <strong>Problems:</strong>
                          <ul className="mb-0 pl-3">
                            {previewResult.problems.map((prob, i) => (
                              <li key={i}>{typeof prob === "string" ? prob : JSON.stringify(prob)}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {Array.isArray(previewResult.warnings) && previewResult.warnings.length > 0 && (
                        <div className="alert alert-warning py-1 px-2 mb-2" style={{ fontSize: "12px" }}>
                          <strong>Warnings:</strong>
                          <ul className="mb-0 pl-3">
                            {previewResult.warnings.map((warn, i) => (
                              <li key={i}>{typeof warn === "string" ? warn : JSON.stringify(warn)}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div>Total Leads: <strong>{previewResult.total ?? previewResult.summary?.totalInputLeads ?? selectedLeads.length}</strong></div>
                      <div className="text-success">Valid / Will be called: <strong>{previewResult.valid ?? previewResult.summary?.validPhoneCount ?? selectedLeads.length}</strong></div>

                      {((previewResult.removed_count && previewResult.removed_count > 0) || (previewResult.removed && previewResult.removed.length > 0)) && (
                        <div className="text-danger mt-1">
                          Removed / Skipped: <strong>{previewResult.removed_count ?? previewResult.removed?.length}</strong>
                          {Array.isArray(previewResult.removed) && previewResult.removed.length > 0 && (
                            <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
                              Reasons: {previewResult.removed.map((r, i) => `${r.name || r.user_phone_number || r.user_identifier || 'Lead'}: ${r.reason || r.error || 'Skipped'}`).join(", ")}
                            </div>
                          )}
                        </div>
                      )}

                      {previewResult.variables?.not_sent && Array.isArray(previewResult.variables.not_sent) && previewResult.variables.not_sent.length > 0 && (
                        <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
                          Variables saved on person: {previewResult.variables.not_sent.join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || (mode === "existing" && !selectedExistingCampaignId)}
                >
                  {loading
                    ? mode === "existing"
                      ? "Adding Contacts..."
                      : "Launching Campaign..."
                    : mode === "existing"
                    ? "Add Contacts to Campaign"
                    : "Launch Campaign"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AICampaignModal;
